import { BILLING_MONEY_EPSILON } from "@/domain/billing/order-billing.constants";
import { roundMoney } from "@/domain/money/round-money";
import type {
  BillingDisplayLine,
  BillingRenderLine,
  OrderBillingPresentation,
  OrderBillingViewModel,
} from "@/domain/billing/order-billing.types";

const INCLUDED_SAVINGS_LABEL = "Included savings";
const DEFAULT_DISCOUNT_LABEL = "Discount";

function isPositiveAmount(amount: number): boolean {
  return amount > BILLING_MONEY_EPSILON;
}

function discountPresentationLabel(line: BillingDisplayLine, source: OrderBillingViewModel["discount"]["source"]): string {
  if (source === "summary") return INCLUDED_SAVINGS_LABEL;
  if (line.label && line.label !== DEFAULT_DISCOUNT_LABEL) {
    return `${INCLUDED_SAVINGS_LABEL} (${line.label})`;
  }
  return INCLUDED_SAVINGS_LABEL;
}

function toRenderLine(line: BillingDisplayLine, vm: OrderBillingViewModel): BillingRenderLine {
  if (line.kind === "discount") {
    return {
      label: discountPresentationLabel(line, vm.discount.source),
      amount: line.amount,
      sign: "negative",
      muted: true,
    };
  }

  return {
    label: line.label,
    amount: line.amount,
    sign: "positive",
    muted: true,
  };
}

/** Collapsed helper text for the billing disclosure header. */
export function getBillingCollapsedSummary(vm: OrderBillingViewModel): string {
  const hasTax = vm.taxLines.length > 0;
  const hasFees = vm.feeLines.length > 0;
  const hasDiscounts = isPositiveAmount(vm.discount.totalAmount);

  if (hasTax && hasFees) return "Includes VAT & fees";
  if (hasTax) return hasDiscounts ? "Includes VAT & discounts" : "Includes VAT";
  if (hasFees) return hasDiscounts ? "Includes fees & discounts" : "Includes fees";
  if (hasDiscounts) return "Includes discounts";
  return "Includes all charges";
}

/** Derives render-only presentation instructions from a billing view model. */
export function deriveOrderBillingPresentation(vm: OrderBillingViewModel): OrderBillingPresentation {
  const detailLines: BillingDisplayLine[] = [
    ...vm.discount.displayLines,
    ...vm.feeLines,
    ...vm.taxLines,
  ];
  const renderLines = detailLines.map((line) => toRenderLine(line, vm));
  const detailLineCount = detailLines.length;
  const hasExpandableLines = vm.hasOrderBillSummary && detailLineCount >= 2;
  const useCompactLayout = vm.hasOrderBillSummary && !hasExpandableLines;
  const compactFooterLine =
    useCompactLayout && detailLineCount === 1 ? toRenderLine(detailLines[0], vm) : null;

  const feeAndTaxLines = [...vm.feeLines, ...vm.taxLines];
  const feesAndTaxAmount =
    feeAndTaxLines.length > 0
      ? roundMoney(feeAndTaxLines.reduce((total, line) => total + line.amount, 0))
      : null;

  return {
    renderLines,
    detailLineCount,
    hasExpandableLines,
    useCompactLayout,
    compactFooterLine,
    collapsedSummaryText: getBillingCollapsedSummary(vm),
    discountForPriceSummary: vm.discount.totalAmount,
    feesAndTaxAmount,
  };
}
